import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "@/components/Reveal";
import { POSTS } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLeft = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yRight = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const vsScale = useTransform(scrollYProgress, [0, 1], [1, 1.35]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16">
      <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
        <motion.div style={{ y: yLeft }} className="relative hidden md:block">
          <div className="absolute inset-0 bg-[#0055FF]/10" />
          <img
            src={POSTS[1].image}
            alt="Windows gaming PC setup"
            className="w-full h-full object-cover opacity-40 grayscale-[30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/40 to-[#0A0A0A]" />
        </motion.div>
        <motion.div style={{ y: yRight }} className="relative hidden md:block">
          <div className="absolute inset-0 bg-[#FF3300]/10" />
          <img
            src={POSTS[4].image}
            alt="Apple iMac on a desk"
            className="w-full h-full object-cover opacity-40 grayscale-[30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A]/40 to-[#0A0A0A]" />
        </motion.div>
        <div className="absolute inset-y-0 left-1/2 w-px bg-white/10 hidden md:block" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-mono-p text-xs tracking-[0.35em] uppercase text-[#A1A1AA] mb-8"
          data-testid="hero-kicker"
        >
          Issue Nº 01 — The Platform Wars, Settled Honestly
        </motion.p>

        <h1 className="font-oswald font-bold uppercase leading-[0.85] tracking-tighter" data-testid="hero-headline">
          <MaskedLine delay={0.4} className="text-6xl sm:text-7xl lg:text-8xl">
            <span className="text-[#F5F5F5]">Windows</span>
          </MaskedLine>
          <span className="relative block py-2 md:py-4">
            <motion.span
              style={{ scale: vsScale }}
              initial={{ opacity: 0, rotate: -8 }}
              animate={{ opacity: 1, rotate: -8 }}
              transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif-d italic normal-case text-5xl sm:text-6xl lg:text-7xl text-stroke inline-block"
              data-testid="hero-vs-mark"
            >
              vs
            </motion.span>
          </span>
          <MaskedLine delay={0.65} className="text-6xl sm:text-7xl lg:text-8xl">
            <span className="text-[#F5F5F5]">Macintosh</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="font-mono-p text-sm sm:text-base text-[#A1A1AA] max-w-xl mx-auto mt-10 leading-relaxed"
          data-testid="hero-subcopy"
        >
          Six rounds. No sponsors. No fence-sitting. The definitive, continuously
          honest comparison of the two machines fighting for your desk.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="mt-10 flex items-center justify-center gap-4"
        >
          <a
            href="#posts"
            data-testid="hero-cta-posts"
            className="font-oswald text-sm tracking-[0.25em] uppercase bg-[#F5F5F5] text-[#0A0A0A] px-8 py-4 hover:bg-[#0055FF] hover:text-white transition-colors duration-300"
          >
            Read the dispatches
          </a>
          <a
            href="#compare"
            data-testid="hero-cta-compare"
            className="font-oswald text-sm tracking-[0.25em] uppercase border border-white/20 px-8 py-4 hover:border-[#FF3300] hover:text-[#FF3300] transition-colors duration-300"
          >
            The scorecard
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono-p text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA]"
      >
        Scroll to begin
      </motion.div>
    </section>
  );
};

export default Hero;
